import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {PageRequest} from '../../shared/models/page-request.interface';
import {Observable, throwError} from 'rxjs';
import {Page} from '../../shared/models/page.interface';
import {ReportCycle} from '../../admin/models/report-cycle.interface';
import {environment} from '../../../environments/environment';
import {Master} from '../../admin/models/master.interface';
import {IdValue} from '../../shared/models/id-value.interface';
import {ReportCycleDetail} from '../../admin/models/report-cycle-detail';
import {ApiHttpResponse} from '../../shared/models/api-http-response.interface';
import {catchError, map} from 'rxjs/operators';
import {ReportCycleSearch} from '../../admin/models/report-cycle-search.interface';

const REPORT_CYCLES_API = environment.apiUrl + 'report-cycles';
const UNU_KEYS_API = environment.apiUrl + 'UNU-keys';
const KF_CATEGORIES_API = environment.apiUrl + 'kf-categories';
const COLLECTION_STREAMS_API = environment.apiUrl + 'collection-streams';

@Injectable({
  providedIn: 'root'
})
export class KfReportingService {

  constructor(private http: HttpClient) {
  }

  static buildSearchParams(search: ReportCycleSearch): string {
    let searchParams = '';
    if (search.startYear && search.startYear !== '') {
      searchParams += `&startYear=${search.startYear}`;
    }
    if (search.endYear && search.endYear !== '') {
      searchParams += `&endYear=${search.endYear}`;
    }
    if (search.unuKeyName && search.unuKeyName !== '') {
      searchParams += `&unuKeyName=${search.unuKeyName}`;
    }
    if (search.kfCategoryName && search.kfCategoryName !== '') {
      searchParams += `&kfCategoryName=${search.kfCategoryName}`;
    }
    if (search.collectionStreamName && search.collectionStreamName !== '') {
      searchParams += `&collectionStreamName=${search.collectionStreamName}`;
    }
    if (search.launched || search.launched === false) {
      searchParams += `&launched=${search.launched}`;
    }
    if (search.startLaunched && search.startLaunched !== '') {
      searchParams += `&startLaunched=${search.startLaunched}`;
    }
    if (search.endLaunched && search.endLaunched !== '') {
      searchParams += `&endLaunched=${search.endLaunched}`;
    }
    if (search.open) {
      searchParams += `&open=${search.open}`;
    }
    return searchParams;
  }

  getAll(pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<ReportCycle>> {
    let url = `${REPORT_CYCLES_API}?size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<ReportCycle>>(url);
  }

  getNexReportYear(): Observable<number> {
    return this.http.get<number>(`${REPORT_CYCLES_API}/next/year`);
  }

  getAllYears(): Observable<number[]> {
    return this.http.get<number[]>(`${REPORT_CYCLES_API}/years`);
  }

  getActiveUnuKeys(pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Master>> {
    let url = `${UNU_KEYS_API}/actives?size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getUnuKeysByPreviousReport(pageRequest: PageRequest, schemaId: number): Observable<Page<Master>> {
    let url = `${UNU_KEYS_API}?schemaId=${schemaId}&size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getActiveCategories(pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Master>> {
    let url = `${KF_CATEGORIES_API}/actives?size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getCategoriesByPreviousReport(pageRequest: PageRequest = {page: 0, size: 100}, schemaId: number): Observable<Page<Master>> {
    let url = `${KF_CATEGORIES_API}?schemaId=${schemaId}&size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getActiveCollectionStreams(pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Master>> {
    let url = `${COLLECTION_STREAMS_API}/actives?size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getCollectionStreamsByPreviousReport(pageRequest: PageRequest = {page: 0, size: 100}, schemaId: number): Observable<Page<Master>> {
    let url = `${COLLECTION_STREAMS_API}?schemaId=${schemaId}&size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getExistingUNUKeys(): Observable<IdValue[]> {
    return this.http.get<IdValue[]>(`${REPORT_CYCLES_API}/existing-UNU-keys`);
  }

  getExistingKfCategories(): Observable<IdValue[]> {
    return this.http.get<IdValue[]>(`${REPORT_CYCLES_API}/existing-kf-categories`);
  }

  getExistingCollectionStreams(): Observable<IdValue[]> {
    return this.http.get<IdValue[]>(`${REPORT_CYCLES_API}/existing-collection-streams`);
  }

  getDraftReportCycle(): Observable<ApiHttpResponse<ReportCycleDetail>> {
    return this.http.get<ApiHttpResponse<ReportCycleDetail>>(`${REPORT_CYCLES_API}/draft`);
  }

  saveReportCycle(reportCycle: ReportCycleDetail): Observable<ApiHttpResponse<ReportCycleDetail>> {
    return this.http.post<ApiHttpResponse<ReportCycleDetail>>(REPORT_CYCLES_API, reportCycle)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<ReportCycleDetail>) => {
          return res;
        })
      );
  }

  launchReportCycle(reportCycle: ReportCycleDetail): Observable<ApiHttpResponse<ReportCycleDetail>> {
    return this.http.post<ApiHttpResponse<ReportCycleDetail>>(`${REPORT_CYCLES_API}/launch`, reportCycle)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  searchReportCycles(search: ReportCycleSearch, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<ReportCycle>> {
    let url = `${REPORT_CYCLES_API}?size=${pageRequest.size}&page=${pageRequest.page}`;
    const searchParams = KfReportingService.buildSearchParams(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<ReportCycle>>(url + searchParams);
  }


  openReportCycle(reportCycle: ReportCycleDetail): Observable<ApiHttpResponse<ReportCycleDetail>> {
    return this.http.post<ApiHttpResponse<ReportCycleDetail>>(`${REPORT_CYCLES_API}/open`, reportCycle)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  printPdf(year: number): Observable<any> {
    return this.http.get(`${REPORT_CYCLES_API}/print?year=${year}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  private getSortCode(sort: string): string {
    switch (sort) {
      case 'open':
        return 'isReportingAvailable';
      default:
        return sort;
    }
  }


}
